import { Dispatch, createAction, createReducer } from '@reduxjs/toolkit';
import { RootState } from 'ducks';
import { injectReducer } from 'ducks/configureStore';
import { Feature, ShouldReset } from 'features/Feature';

/**
 * This feature manages the state for adding multiple extensions to BILLY.
 * It provides functionality to remember the user's choice and toggle the ability to multiple extensions.
 */
interface AddMultipleFrameExtensions {
  rememberChoice: boolean;
  addMultiple: boolean;
}

const initialState: AddMultipleFrameExtensions = {
  rememberChoice: false,
  addMultiple: false
};

type ExtendedRootState = RootState & {
  addMultipleFrameExtensions?: AddMultipleFrameExtensions;
};

/**
 * Action to set the "add multiple frame extensions" state.
 * @param payload - A boolean indicating whether to enable or disable the functionality.
 */
export const setAddMultipleFrameExtensions = createAction<boolean>(
  'addMultipleFrameExtensions/setAddMultipleFrameExtensions'
);

/**
 * Action to reset the "add multiple frame extensions" state to its initial values.
 */
export const resetAddMultipleFrameExtensions = createAction(
  'addMultipleFrameExtensions/resetAddMultipleFrameExtensions'
);

/**
 * Selector to retrieve the `rememberChoice` value from the state.
 * @param state - The application state.
 * @returns A boolean indicating whether the user's choice is remembered.
 */
export const rememberAddMultipleFrameExtensions = (state: ExtendedRootState) =>
  state.addMultipleFrameExtensions?.rememberChoice;

/**
 * Selector to retrieve the `addMultiple` value from the state.
 * @param state - The application state.
 * @returns A boolean indicating whether the "add multiple frame extensions" functionality is enabled.
 */
export const addMultipleFrameExtensions = (state: ExtendedRootState) =>
  state.addMultipleFrameExtensions?.addMultiple;

/**
 * Reducer to manage the state transitions for the "add multiple frame extensions" feature.
 */
const addMultipleFrameExtensionsReducer = createReducer(initialState, builder => {
  builder.addCase(setAddMultipleFrameExtensions, (state, action) => {
    state.rememberChoice = true;
    state.addMultiple = action.payload;
  });
  builder.addCase(resetAddMultipleFrameExtensions, state => {
    state.rememberChoice = false;
    state.addMultiple = false;
  });
});

/**
 * Feature class for managing the "add multiple frame extensions" functionality.
 * This class initializes the reducer and provides a method to reset the state.
 */
export class AddMultipleFrameExtensionsFeature extends Feature implements ShouldReset {
  /**
   * Initializes the feature by injecting the reducer into the store.
   */
  protected init(): void {
    injectReducer<AddMultipleFrameExtensions>(
      'addMultipleFrameExtensions',
      addMultipleFrameExtensionsReducer
    );
  }

  /**
   * Resets the state for the "add multiple frame extensions" feature.
   * @param dispatch - The Redux dispatch function.
   */
  reset(dispatch: Dispatch): void {
    dispatch(resetAddMultipleFrameExtensions());
  }
}
